import { StatusData } from "@fairfleet/geotab";

/** One point in time, with a value per selected diagnostic that reported at it. */
export type ChartPoint = Record<string, number> & { timestamp: number };

export interface ChartDataset {
  points: ChartPoint[];
  /** Diagnostics in `points` whose readings are booleans (on/off state) rather than numeric measurements — the caller must chart these on their own axis, not the numeric one. */
  booleanDiagnosticIds: Set<string>;
}

const EMPTY: ChartDataset = {
  points: [],
  booleanDiagnosticIds: new Set(),
};

/**
 * Pivots the readings into what a line chart wants: one row per instant, one column per series.
 *
 * Readings arrive as a flat list of (diagnostic, time, value). Two diagnostics rarely report at
 * the same instant, so most rows carry a value for one series and nothing for the others — which
 * is what makes the chart draw a line through the gaps rather than back to zero.
 *
 * @param statusData - Every reading in the period, in any order.
 * @param diagnosticIds - The diagnostics to chart.
 * @returns The points in time order.
 */
export function buildChartDataset(statusData: StatusData[], diagnosticIds: string[]): ChartDataset {
  if (diagnosticIds.length === 0) {
    return EMPTY;
  }

  const wanted = new Set(diagnosticIds);
  const byTimestamp = new Map<number, ChartPoint>();
  const booleanDiagnosticIds = new Set<string>();

  for (const entry of statusData) {
    const diagnosticId = entry.diagnostic?.id;
    if (!diagnosticId || !wanted.has(diagnosticId)) {
      continue;
    }

    const timestamp = new Date(entry.dateTime).getTime();
    if (!Number.isFinite(timestamp)) {
      continue;
    }

    let value: number;
    if (typeof entry.data === "number") {
      if (!Number.isFinite(entry.data)) {
        continue;
      }
      value = entry.data;
    } else if (typeof entry.data === "boolean") {
      value = entry.data ? 1 : 0;
      booleanDiagnosticIds.add(diagnosticId);
    } else {
      continue;
    }

    const point = byTimestamp.get(timestamp) ?? ({ timestamp } as ChartPoint);
    point[diagnosticId] = value;
    byTimestamp.set(timestamp, point);
  }

  const points = [...byTimestamp.values()].sort((a, b) => a.timestamp - b.timestamp);

  return { points, booleanDiagnosticIds };
}
